import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';

const ForgotPasswordPage: FC = () => {
  const navigate = useNavigate();
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setError('');
    setLoading(true);
    try {
      await dispatch(actions.forgotPassword, { email: email.trim() });
      setSent(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        <div className="rounded-2xl border border-border bg-card shadow-card-md p-8">

          <div className="mb-8 flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
              <Zap className="h-6 w-6 text-primary" strokeWidth={2.5} />
            </div>
            <div className="text-center">
              <h1 className="text-xl font-bold text-foreground">Forgot password?</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Enter your email and we'll send a reset link
              </p>
            </div>
          </div>

          {sent ? (
            <div className="flex flex-col items-center gap-4 py-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-center text-sm text-foreground font-medium">
                Check your inbox
              </p>
              <p className="text-center text-xs text-muted-foreground">
                If that email is registered, a reset link has been sent. It expires in 1 hour.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="mt-2 w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 transition-all"
              >
                Back to Sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-xs text-red-700">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-foreground">Email address</label>
                <input
                  type="email" value={email} autoFocus
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-border px-3 py-2.5 text-sm bg-background outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>

              <button
                type="submit" disabled={loading}
                className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-primary/90 disabled:opacity-60 transition-all mt-2"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )}

          {!sent && (
            <button
              onClick={() => navigate('/login')}
              className="mt-5 flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Sign in
            </button>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          JumiaDash — Seller Analytics Platform
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
